import React, { useState } from 'react';
import { MdOutlineRemoveRedEye, MdVisibilityOff } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { API } from 'utilities/api';
import { toast } from 'react-toastify';
import { T, inputFocus, inputBlur } from './authStyles';
import OAuthButtons from './OAuthButtons';
import Divider from './Divider';

/**
 * Email/password sign-up with a verification code step.
 *
 * Flow mirrors SignIn:
 *   1. signUp(...)                          → backend creates user, emails code
 *   2. verificationMail(email, code)        → backend returns Token + user
 *   3. login(token, user) + onSuccess(user) → modal routes the redirect
 *
 * `onSuccess` receives the same user payload shape as SignIn so the
 * parent's routing logic can be a single code path.
 */
export default function SignUp({ onSuccess }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyStep, setVerifyStep] = useState(false);
  const [code, setCode] = useState('');
  const { login } = useAuth();

  const handleSignUp = async () => {
    if (!firstName || !lastName || !email || !password) { toast.error('Please fill all fields'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await API.signUp({ email_address: email, password, first_name: firstName, last_name: lastName }, 'none');
      setVerifyStep(true);
      toast.success('Account created! Check your email for a verification code.');
    } catch (err) { toast.error(err?.message || 'Sign up failed'); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!code) { toast.error('Enter the verification code'); return; }
    setLoading(true);
    try {
      const res = await API.verificationMail({ email_address: email, confirmationCode: code });
      const token = res?.Token || res?.token;
      if (!token) { toast.error('Verification failed. Please try again.'); return; }
      login(token, res.user || null);
      toast.success('Welcome to TechProOne!');
      onSuccess?.(res.user || null);
    } catch (err) { toast.error(err?.message || 'Invalid verification code'); }
    finally { setLoading(false); }
  };

  if (verifyStep) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', margin: 0 }}>
        Verification code sent to <b>{email}</b>
      </p>
      <input style={T.input} placeholder="Verification code" value={code} onChange={e => setCode(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
      <button style={{ ...T.primaryBtn, opacity: loading ? 0.7 : 1 }} onClick={handleVerify} disabled={loading}>{loading ? 'Verifying…' : 'Verify Email'}</button>
      <button style={{ background: 'none', border: 'none', color: '#A78BFA', fontSize: 13, cursor: 'pointer' }} onClick={() => API.resendVerificationMail({ email_address: email })}>Resend code</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <OAuthButtons onSuccess={onSuccess} />
      <Divider label="or sign up with email" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={T.label}>First Name</label>
          <input style={T.input} placeholder="John" value={firstName} onChange={e => setFirstName(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
        </div>
        <div>
          <label style={T.label}>Last Name</label>
          <input style={T.input} placeholder="Doe" value={lastName} onChange={e => setLastName(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
        </div>
      </div>
      <div>
        <label style={T.label}>Email</label>
        <input style={T.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
      </div>
      <div>
        <label style={T.label}>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...T.input, paddingRight: 44 }}
            type={showPass ? 'text' : 'password'}
            placeholder="Min. 8 characters"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={inputFocus}
            onBlur={inputBlur}
          />
          <button onClick={() => setShowPass(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center' }}>
            {showPass ? <MdVisibilityOff size={18} /> : <MdOutlineRemoveRedEye size={18} />}
          </button>
        </div>
      </div>
      <button
        style={{ ...T.primaryBtn, opacity: loading ? 0.7 : 1 }}
        onClick={handleSignUp}
        disabled={loading}
        onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
        onMouseLeave={e => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; e.currentTarget.style.transform = 'none'; }}
      >
        {loading ? 'Creating account…' : 'Create Account'}
      </button>
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, textAlign: 'center', margin: 0 }}>
        By signing up you agree to our Terms & Privacy Policy
      </p>
    </div>
  );
}
